/**
 * The nudge flow shared by Triage and Routed away (kanban 68cc2cfb): a
 * Nudge button opens a confirm step under the row (a message to a person
 * is never one click, the way Pull back asks first), Send calls the
 * backend, the item and the ask rows it rode on are applied to the shell's
 * state so "nudged <date>" shows at once, and a refusal is shown under the
 * row in the backend's words. The per-person nudge sends one item after
 * another and reports how many went.
 */
import { useState } from 'react';
import { failedActionMessage } from '../../components/portal/investigationsView';
import { trackEvent } from '../../services/portalTracking';
import { nudgeThread, type DoorThreadRead } from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { nudgePersonSummary, type NudgeTarget } from './nudgeView';
import { personKey } from './listFilter';

export type NudgeFrom = 'triage' | 'routed_away';

export interface NudgeNote {
  /** The item (thread id) or person (`person:<key>`) the note sits under. */
  key: string;
  message: string;
  tone: 'error' | 'done';
}

export interface NudgeApi {
  /** The target whose confirm step is open, if any. */
  pending: NudgeTarget | null;
  /** The person whose "about all N" confirm step is open, if any. */
  personPending: { who: string; targets: NudgeTarget[] } | null;
  /** The item (thread id) or person key in flight. */
  busy: string | null;
  /** A refusal or a per-person summary, under the row or the person. */
  note: NudgeNote | null;
  open: (target: NudgeTarget) => void;
  openPerson: (who: string, targets: NudgeTarget[]) => void;
  cancel: () => void;
  send: (target: NudgeTarget) => Promise<boolean>;
  sendPerson: () => Promise<void>;
  noteFor: (key: string) => NudgeNote | null;
}

export function personNoteKey(who: string): string {
  return `person:${personKey(who)}`;
}

function refusal(target: NudgeTarget, e: unknown): string {
  return failedActionMessage(`nudge ${target.who} about ${target.thread.thread_no}`, e);
}

export function useNudge(
  from: NudgeFrom,
  apply: {
    thread: (t: DoorThreadRead) => void;
    request?: (r: DoorDataRequestRead) => void;
  },
): NudgeApi {
  const [pending, setPending] = useState<NudgeTarget | null>(null);
  const [personPending, setPersonPending] = useState<{ who: string; targets: NudgeTarget[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<NudgeNote | null>(null);

  const open = (target: NudgeTarget) => {
    setNote(null);
    setPersonPending(null);
    setPending(target);
    trackEvent('action', 'qcp_nudge_open', { thread_no: target.thread.thread_no, who: target.who, from });
  };
  const openPerson = (who: string, targets: NudgeTarget[]) => {
    setNote(null);
    setPending(null);
    setPersonPending({ who, targets });
    trackEvent('action', 'qcp_nudge_person_open', { who, count: targets.length, from });
  };
  const cancel = () => {
    const was = pending?.thread.thread_no ?? personPending?.who ?? null;
    setPending(null);
    setPersonPending(null);
    trackEvent('action', 'qcp_nudge_cancel', { target: was, from });
  };

  /** One nudge; the outcome is applied to the shell's state. Returns the
   *  refusal text when it did not go. */
  const run = async (target: NudgeTarget): Promise<string | null> => {
    try {
      const result = await nudgeThread(target.thread.id, target.who);
      apply.thread(result.thread);
      for (const r of result.requests) apply.request?.(r);
      trackEvent('action', 'qcp_nudge', {
        thread_no: target.thread.thread_no,
        who: target.who,
        via: result.via,
        from,
        outcome: 'ok',
      });
      return null;
    } catch (e) {
      trackEvent('action', 'qcp_nudge', {
        thread_no: target.thread.thread_no,
        who: target.who,
        from,
        outcome: 'refused',
      });
      return refusal(target, e);
    }
  };

  const send = async (target: NudgeTarget): Promise<boolean> => {
    if (busy) return false;
    setBusy(target.thread.id);
    setNote(null);
    const problem = await run(target);
    setBusy(null);
    setPending(null);
    if (problem) {
      setNote({ key: target.thread.id, message: problem, tone: 'error' });
      return false;
    }
    return true;
  };

  const sendPerson = async () => {
    if (busy || !personPending) return;
    const { who, targets } = personPending;
    const key = personNoteKey(who);
    setBusy(key);
    setNote(null);
    let sent = 0;
    const refused: string[] = [];
    for (const target of targets) {
      const problem = await run(target);
      if (problem) refused.push(`${target.thread.thread_no}: ${problem}`);
      else sent += 1;
    }
    setBusy(null);
    setPersonPending(null);
    trackEvent('action', 'qcp_nudge_person', { who, sent, refused: refused.length, from });
    setNote({ key, message: nudgePersonSummary(who, sent, refused), tone: refused.length ? 'error' : 'done' });
  };

  const noteFor = (key: string) => (note && note.key === key ? note : null);

  return { pending, personPending, busy, note, open, openPerson, cancel, send, sendPerson, noteFor };
}
